import "./App.css";
import React, {useEffect, useState} from "react";
import {Calendar, defaultTheme, Provider, useDateFormatter, useLocale,} from "@adobe/react-spectrum";
import {useNavigate, useSearchParams} from "react-router-dom";
import {getDayOfWeek, getLocalTimeZone, parseDate, today} from "@internationalized/date";
import axios from "axios";
import {useDispatch, useSelector} from "react-redux";
import {dateActions} from "./store/service";
import {Button, Card, CardBody, IconButton, Typography} from "@material-tailwind/react";
import {Calendar as CalendarIcon, CheckCircle, Clock} from "lucide-react";
import Footer from "./footer";
import SummaryPanel from "./SummaryPanel";
import MobileNextButton from "./MobileNextButton";

function Schedule() {
    // Scroll to top on mount
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const navigate = useNavigate();
    const [date, setDate] = React.useState(
        parseDate(new Date(new Date().getTime() + (24 * 60 * 60 * 1000) - (new Date().getTimezoneOffset() * 60000)).toISOString().substring(0, 10))
    );
    let formatter = useDateFormatter({dateStyle: "full"});
    const [searchParams, setSearchParams] = useSearchParams();
    let {locale} = useLocale();

    const [timeSlots, setTimeSlots] = useState([]);
    const [progress, setProgress] = useState(false);
    const [isTimeSelected, setIsTimeSelected] = useState(false);
    const [branch, setBranch] = useState(null);

    const dispatch = useDispatch();
    const dateList = useSelector((state) => state.date);

    const [serviceList, setServiceList] = useState([]);

    const updateServiceList = () => {
        setServiceList(JSON.parse(localStorage.getItem("services")));
    };

    const getAddress = async () => {
        //localStorage.clear();
        try {
            await axios
                .get(process.env.REACT_APP_API_URL + "/api/v1/getAddress", {
                    withCredentials: true,
                })
                .then((response) => {
                    setBranch(response.data);
                })
                .catch((error) => {
                    console.log(error);
                    navigate("/");
                });
        } catch (e) {
            console.log(e);
        }
    };

    const getAvailableTimeslots = async () => {
        const checkboxes = document.getElementsByName("serviceTime");
        for (var i = 0; i < checkboxes.length - 1; i++) {
            checkboxes[i].checked = false;
        }

        try {
            const serviceIDs = [];

            const serviceArray = JSON.parse(localStorage.getItem("services"));

            for (let i = 0; i < serviceArray.length; i++) {
                serviceIDs.push(serviceArray[i].id);
            }

            const month = date.month < 10 ? "0" + date.month : date.month.toString();
            const day = date.day < 10 ? "0" + date.day : date.day.toString();

            const postData = {
                serviceIDs: serviceIDs,
                pickedDate: date.year + "-" + month + "-" + day,
                branchID: 1,
            };

            try {
                setTimeSlots([]);
                setProgress(true);
                await new Promise((resolve) => setTimeout(resolve, 1100));

                await axios
                    .post(
                        process.env.REACT_APP_API_URL + "/api/v1/getAvailableTime",
                        postData,
                        {withCredentials: true}
                    )
                    .then((response) => {
                        setTimeSlots(response.data);
                        setIsTimeSelected(false);
                        updateServiceList();

                        const params = new URLSearchParams(searchParams);
                        params.delete("date");
                        params.delete("time");
                        setProgress(true);
                    })
                    .catch((error) => {
                        console.log(error);
                        navigate("/");
                    });
            } catch (e) {
                console.log(e);
            }
        } catch (error) {
            console.error("Error deleting post:", error);
        }

        setProgress(false);
    };

    useEffect(() => {
        getAvailableTimeslots();
        getAddress();
    }, [date]);

    const timeAddHandler = (timeSlot) => {
        const year = date.year;
        const month = date.month < 10 ? "0" + date.month : date.month.toString();
        const day = date.day < 10 ? "0" + date.day : date.day.toString();
        const date2 = year + "-" + month + "-" + day;
        const dateTimeString = date2 + " " + timeSlot;

        dispatch(dateActions.add(dateTimeString));
        localStorage.setItem("bookingtime", dateTimeString);

        setIsTimeSelected(true);
    };


    let now = today(getLocalTimeZone());
    //오늘 날짜가 지난 경우 예약 불가
    // 오늘 날짜로 부터 60일 이후 예약 불가 처리
    let disabledRanges = [
        [now.subtract({years: 100}), now.subtract({days: 0})],
        [now.add({days: 60}), now.add({years: 100})],
    ];

    let isDateUnavailable = (date) =>
        getDayOfWeek(date, locale) === 0 ||
        disabledRanges.some(
            (interval) =>
                date.compare(interval[0]) >= 0 && date.compare(interval[1]) <= 0
        );

    return (
        <>
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-50 scroll-container" style={{WebkitOverflowScrolling: 'touch', overscrollBehavior: 'none'}}>
                <div className="container mx-auto max-w-7xl px-4 py-4 sm:py-6">
                    {/* Enhanced Header with back button */}
                    <div className="mb-6 sm:mb-8">
                        <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                            <IconButton
                                variant="outlined"
                                color="blue-gray"
                                size="lg"
                                onClick={() => navigate("/services")}
                                className="border-gray-300 hover:bg-gray-50 transition-all duration-300"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth={2}
                                    stroke="currentColor"
                                    className="w-5 h-5 sm:w-6 sm:h-6"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
                                    />
                                </svg>
                            </IconButton>
                            <div>
                                <Typography variant="h4" color="blue-gray"
                                            className="font-bold text-xl sm:text-2xl lg:text-3xl">
                                    Select Date & Time
                                </Typography>
                                <Typography variant="paragraph" color="gray" className="mt-1 text-sm sm:text-base">
                                    Choose your preferred appointment time
                                </Typography>
                            </div>
                        </div>

                        {/* Progress indicator */}
                        <div className="flex items-center justify-center mb-6 sm:mb-8 overflow-x-auto px-2 sm:px-4 overflow-y-hidden">
                            <div className="flex items-center justify-between w-full max-w-4xl">
                                {/* Step 1 - Select Services */}
                                <div className="flex items-center justify-center">
                                    <div className="flex items-center cursor-pointer" onClick={() => navigate("/services")}>
                                        <div
                                            className="w-6 h-6 sm:w-6 sm:h-6 md:w-8 md:h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-xs sm:text-sm flex-shrink-0 cursor-pointer hover:bg-green-600 transition-colors duration-300"

                                        >
                                            <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5"/>
                                        </div>
                                        {/* Text label - hidden on mobile */}
                                        <Typography variant="small"
                                                    className="ml-1 sm:ml-2 text-green-600 text-xs sm:text-sm whitespace-nowrap hidden sm:block font-medium">
                                            1. Services
                                        </Typography>
                                    </div>
                                </div>

                                {/* Connector line 1 */}
                                <div className="flex-1 h-0.5 bg-blue-500 mx-2 sm:mx-4"></div>

                                {/* Step 2 - Choose Schedule (CURRENT) */}
                                <div className="flex items-center justify-center relative">
                                    <div className="relative">
                                        {/* Pulsing ring animation - hidden on mobile */}
                                        <div className="absolute inset-0 w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-blue-500 rounded-full animate-ping opacity-20 hidden sm:block"></div>
                                        <div className="absolute inset-0 w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-blue-500 rounded-full animate-pulse opacity-30 hidden sm:block"></div>

                                        {/* Main step indicator */}
                                        <div className="relative w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-full flex items-center justify-center text-sm sm:text-base md:text-lg font-bold shadow-lg border-2 sm:border-4 border-white flex-shrink-0 z-10">
                                            2
                                        </div>
                                    </div>

                                    {/* Current step indicator - hidden on mobile */}
                                    <div className="ml-2 sm:ml-3 hidden sm:block">
                                        <Typography variant="small"
                                                    className="font-bold text-blue-600 text-sm sm:text-base whitespace-nowrap">
                                            Schedule
                                        </Typography>
                                        <div className="text-xs text-blue-500 font-medium">
                                            Current Step
                                        </div>
                                    </div>
                                </div>

                                {/* Connector line 2 */}
                                <div className="flex-1 h-0.5 bg-gray-300 mx-2 sm:mx-4"></div>

                                {/* Step 3 - Car Information */}
                                <div className="flex items-center justify-center">
                                    <div
                                        className="w-6 h-6 sm:w-6 sm:h-6 md:w-8 md:h-8 bg-gray-300 text-gray-600 rounded-full flex items-center justify-center text-xs sm:text-sm flex-shrink-0">
                                        3
                                    </div>
                                    {/* Text label - hidden on mobile */}
                                    <Typography variant="small"
                                                className="ml-1 sm:ml-2 text-gray-500 text-xs sm:text-sm whitespace-nowrap hidden sm:block">
                                        Car
                                    </Typography>
                                </div>

                                {/* Connector line 3 */}
                                <div className="flex-1 h-0.5 bg-gray-300 mx-2 sm:mx-4"></div>

                                {/* Step 4 - Contact Information */}
                                <div className="flex items-center justify-center">
                                    <div
                                        className="w-6 h-6 sm:w-6 sm:h-6 md:w-8 md:h-8 bg-gray-300 text-gray-600 rounded-full flex items-center justify-center text-xs sm:text-sm flex-shrink-0">
                                        4
                                    </div>
                                    {/* Text label - hidden on mobile */}
                                    <Typography variant="small"
                                                className="ml-1 sm:ml-2 text-gray-500 text-xs sm:text-sm whitespace-nowrap hidden sm:block">
                                        Contact
                                    </Typography>
                                </div>

                                {/* Connector line 4 */}
                                <div className="flex-1 h-0.5 bg-gray-300 mx-2 sm:mx-4"></div>

                                {/* Step 5 - Complete */}
                                <div className="flex items-center justify-center">
                                    <div
                                        className="w-6 h-6 sm:w-6 sm:h-6 md:w-8 md:h-8 bg-gray-300 text-gray-600 rounded-full flex items-center justify-center text-xs sm:text-sm flex-shrink-0">
                                        5
                                    </div>
                                    {/* Text label - hidden on mobile */}
                                    <Typography variant="small"
                                                className="ml-1 sm:ml-2 text-gray-500 text-xs sm:text-sm whitespace-nowrap hidden sm:block">
                                        Complete
                                    </Typography>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 justify-center">
                        {/* Enhanced Calendar and Time Selection Section */}
                        <div className="flex-1 max-w-5xl">
                            <Card className="border-0 shadow-xl bg-white/80 backdrop-blur-sm">
                                <CardBody className="p-4 sm:p-6 lg:p-8">
                                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
                                        {/* Calendar and Time Selection */}
                                        <div className="lg:col-span-2">
                                            <div className="mb-6 sm:mb-8">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <CalendarIcon className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600"/>
                                                    <Typography variant="h5" color="blue-gray"
                                                                className="font-bold text-lg sm:text-xl lg:text-2xl">
                                                        Select Date
                                                    </Typography>
                                                </div>
                                                {/*<Typography variant="paragraph" color="gray"*/}
                                                {/*            className="mb-4 sm:mb-6 text-sm sm:text-base">*/}
                                                {/*    Choose your preferred appointment date from the calendar below.*/}
                                                {/*</Typography>*/}
                                            </div>

                                            {/* Enhanced Calendar */}
                                            <div className="mb-6 sm:mb-8 flex justify-center">
                                                <div
                                                    className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 sm:p-6">
                                                    <Provider theme={defaultTheme} colorScheme="light">
                                                        <Calendar
                                                            aria-label="Event date"
                                                            value={date}
                                                            isDateUnavailable={isDateUnavailable}
                                                            onChange={setDate}
                                                            UNSAFE_className="modern-calendar"
                                                            UNSAFE_style={{
                                                                fontSize: "1.25rem",
                                                                "--spectrum-calendar-day-width": "3rem",
                                                                "--spectrum-calendar-day-height": "3rem",
                                                                "--spectrum-global-color-gray-700": "#334155",
                                                                "--spectrum-alias-background-color-primary": "#f8fafc",
                                                                "--spectrum-global-color-blue-600": "#2563eb",
                                                                "--spectrum-alias-text-color": "#0f172a",
                                                                "--spectrum-calendar-day-border-radius": "8px",
                                                            }}
                                                        />
                                                    </Provider>
                                                </div>
                                            </div>

                                            {/* Enhanced Time Selection */}
                                            <div className="mb-6 sm:mb-8">
                                                <div className="flex items-center gap-2 mb-4">
                                                    <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600"/>
                                                    <Typography variant="h5" color="blue-gray"
                                                                className="font-bold text-lg sm:text-xl lg:text-2xl">
                                                        Available Times
                                                    </Typography>
                                                </div>
                                                {/*<Typography variant="paragraph" color="gray"*/}
                                                {/*            className="mb-4 sm:mb-6 text-sm sm:text-base">*/}
                                                {/*    {formatter.format(date.toDate(getLocalTimeZone()))} - Select your*/}
                                                {/*    preferred time slot.*/}
                                                {/*</Typography>*/}

                                                {progress ? (
                                                    <div className="space-y-4">
                                                        {[1, 2, 3, 4, 5, 6].map((i) => (
                                                            <div key={i} className="animate-pulse">
                                                                <div className="h-16 bg-gray-200 rounded-xl"></div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div className="grid grid-cols-2 sm:grid-cols-2 gap-3 sm:gap-4">
                                                        {timeSlots?.map((slots, index) => (
                                                            <div key={slots.timeSlot} className="relative">
                                                                <input
                                                                    type="radio"
                                                                    id={index}
                                                                    value={slots.timeSlot}
                                                                    name="serviceTime"
                                                                    className="hidden peer"
                                                                    onClick={() => timeAddHandler(slots.timeSlot)}
                                                                />
                                                                <label
                                                                    htmlFor={index}
                                                                    className="group relative flex items-center justify-center w-full h-16 sm:h-18 p-4 sm:p-5 text-gray-700 bg-white border-2 border-gray-200 rounded-xl cursor-pointer transition-all duration-300 peer-checked:border-green-500 peer-checked:bg-green-50 peer-checked:shadow-md hover:bg-gray-50 hover:border-gray-300 hover:shadow-md transform hover:-translate-y-1 active:scale-95"
                                                                >
                                                                    <div className="flex items-center gap-3 w-full">
                                                                        <div
                                                                            className="w-8 h-8 bg-gradient-to-br from-green-100 to-green-200 rounded-full flex items-center justify-center peer-checked:from-green-500 peer-checked:to-green-600 group-hover:from-green-200 group-hover:to-green-300 transition-all duration-300">
                                                                            <Clock
                                                                                className="w-4 h-4 text-green-600 peer-checked:text-white"/>
                                                                        </div>
                                                                        <Typography
                                                                            className="font-bold text-center text-lg sm:text-base">
                                                                            {slots.timeSlot}
                                                                        </Typography>
                                                                    </div>

                                                                    {/* Checkmark indicator */}
                                                                    {/*<div className="absolute top-2 right-2 w-5 h-5 bg-white border-2 border-gray-300 rounded-full peer-checked:bg-blue-500 peer-checked:border-blue-500 transition-all duration-300">*/}
                                                                    {/*    <svg*/}
                                                                    {/*        xmlns="http://www.w3.org/2000/svg"*/}
                                                                    {/*        fill="none"*/}
                                                                    {/*        viewBox="0 0 24 24"*/}
                                                                    {/*        strokeWidth={3}*/}
                                                                    {/*        stroke="currentColor"*/}
                                                                    {/*        className="w-3 h-3 text-white opacity-0 peer-checked:opacity-100 transition-opacity duration-300"*/}
                                                                    {/*    >*/}
                                                                    {/*        <path*/}
                                                                    {/*            strokeLinecap="round"*/}
                                                                    {/*            strokeLinejoin="round"*/}
                                                                    {/*            d="M4.5 12.75l6 6 9-13.5"*/}
                                                                    {/*        />*/}
                                                                    {/*    </svg>*/}
                                                                    {/*</div>*/}
                                                                </label>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            <MobileNextButton
                                                link="/carinfo"
                                                enabled={isTimeSelected}
                                                buttonText="Next Step →"
                                                disabledText="Please select a time"
                                            />
                                        </div>
                                        {/* Show SummaryPanel only on non-mobile (lg and up) screens */}
                                        <div className="lg:col-span-1 order-first lg:order-last hidden lg:block">
                                            <SummaryPanel
                                                link={"/carinfo"}
                                                branch={branch}
                                                selectedServiceList={serviceList}
                                                dateList={dateList}
                                                enabled={dateList && dateList.length > 0}
                                            />
                                        </div>

                                    </div>
                                </CardBody>
                            </Card>
                        </div>
                    </div>
                </div>
                <Footer/>
            </div>
        </>
    );
}

export default Schedule;
